/**
 * InterviewTranscriptView — chat-style render of an AI interview transcript
 * stored in source.markdownContent (`**Speaker:** content\n\n...` format).
 *
 * Highlights selected facet text inline so the facet review flow keeps working
 * for interview sources, but lays the conversation out as bubbles instead of a
 * flat doc.
 */

import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { MessageSquare, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DetectedFacet } from '@/lib/api/client'

interface InterviewTranscriptViewProps {
  content: string
  facets: DetectedFacet[]
  selectedFacetId: string | null
  onFacetClick: (id: string) => void
  intervieweeName?: string
  filterCategory?: string
  filterStatus?: string
}

interface TranscriptTurn {
  speaker: string
  role: 'interviewer' | 'interviewee'
  text: string
}

const TURN_PATTERN = /^\*\*(.+?):\*\*\s*([\s\S]*)$/

function parseTranscript(content: string, intervieweeName?: string): TranscriptTurn[] {
  if (!content) return []
  return content
    .split(/\n{2,}/)
    .map(block => block.trim())
    .filter(Boolean)
    .map<TranscriptTurn | null>(block => {
      const match = block.match(TURN_PATTERN)
      if (!match) return null
      const speaker = match[1].trim()
      const text = match[2].trim()
      const isInterviewer = /^(interviewer|ai|skerpa|host)$/i.test(speaker)
      return {
        speaker,
        role: isInterviewer ? 'interviewer' : 'interviewee',
        text,
      }
    })
    .filter((t): t is TranscriptTurn => t !== null)
    .map(turn => {
      // If we know the interviewee name and the speaker matches, normalize the role
      if (intervieweeName && turn.speaker.toLowerCase() === intervieweeName.toLowerCase()) {
        return { ...turn, role: 'interviewee' as const }
      }
      return turn
    })
}

interface FacetSpan {
  start: number
  end: number
  facet: DetectedFacet
}

function findFacetSpans(text: string, facets: DetectedFacet[]): FacetSpan[] {
  const spans: FacetSpan[] = []
  for (const facet of facets) {
    const evidence = facet.evidence?.trim()
    if (!evidence || evidence.length < 4) continue
    const idx = text.toLowerCase().indexOf(evidence.toLowerCase())
    if (idx === -1) continue
    spans.push({ start: idx, end: idx + evidence.length, facet })
  }
  // Sort and dedupe overlaps (keep the longest match)
  spans.sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start))
  const merged: FacetSpan[] = []
  for (const span of spans) {
    const last = merged[merged.length - 1]
    if (last && span.start < last.end) continue
    merged.push(span)
  }
  return merged
}

function renderTurnText(
  text: string,
  facets: DetectedFacet[],
  selectedFacetId: string | null,
  onFacetClick: (id: string) => void,
): React.ReactNode {
  const spans = findFacetSpans(text, facets)
  if (spans.length === 0) return text

  const nodes: React.ReactNode[] = []
  let cursor = 0
  spans.forEach((span, i) => {
    if (span.start > cursor) nodes.push(text.slice(cursor, span.start))
    const isSelected = span.facet.id === selectedFacetId
    nodes.push(
      <button
        key={`${span.facet.id}-${i}`}
        type="button"
        onClick={() => onFacetClick(span.facet.id)}
        className={cn(
          'rounded px-0.5 py-px text-left transition-colors focus:outline-none focus:ring-2 focus:ring-accent-300',
          isSelected
            ? 'bg-accent-200 text-fg'
            : 'bg-warning-bg text-fg hover:bg-warning-bg/80',
        )}
        title={span.facet.facetTypeId}
      >
        {text.slice(span.start, span.end)}
      </button>,
    )
    cursor = span.end
  })
  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

export function InterviewTranscriptView({
  content,
  facets,
  selectedFacetId,
  onFacetClick,
  intervieweeName,
  filterCategory,
  filterStatus,
}: InterviewTranscriptViewProps) {
  const turns = useMemo(() => parseTranscript(content, intervieweeName), [content, intervieweeName])

  const visibleFacets = useMemo(() => {
    return facets.filter(f => {
      if (filterStatus && filterStatus !== 'all' && f.status !== filterStatus) return false
      if (filterCategory && filterCategory !== 'all' && f.facetTypeId !== filterCategory) return false
      return true
    })
  }, [facets, filterCategory, filterStatus])

  if (turns.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-12 text-center">
        <div className="space-y-2">
          <MessageSquare className="mx-auto h-6 w-6 text-fg-muted" />
          <p className="text-body text-fg-muted">No transcript available.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto h-full max-w-3xl overflow-y-auto px-6 py-8">
      <div className="mb-6 flex items-center gap-2 text-caption text-fg-muted">
        <Sparkles className="h-3.5 w-3.5" />
        <span>{turns.length} turns · {visibleFacets.length} highlighted insights</span>
      </div>
      <div className="space-y-5">
        {turns.map((turn, i) => {
          const isInterviewer = turn.role === 'interviewer'
          return (
            <div key={i} className="space-y-1.5">
              <Badge
                variant="outline"
                className={cn(
                  'rounded-sm border-none px-0 text-[10px] font-medium uppercase tracking-wide',
                  isInterviewer ? 'text-fg-muted' : 'text-market-700',
                )}
              >
                {turn.speaker}
              </Badge>
              <div
                className={cn(
                  'whitespace-pre-wrap text-body leading-relaxed',
                  isInterviewer ? 'text-fg-secondary' : 'text-fg',
                )}
              >
                {renderTurnText(turn.text, visibleFacets, selectedFacetId, onFacetClick)}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
